import {
  compact,
  differenceBy,
  filter,
  find,
  flatMap,
  forEach,
  get,
  has,
  invoke,
  isBoolean,
  isEmpty,
  isFunction,
  map,
  omit,
  reject,
  set,
} from 'lodash';
import { BUTTON_TEMPLATES } from '@app/text-content';
import {
  GRID_ROW_MODEL_TYPE,
  TOOLBAR_BUTTON_POSITION,
  ToolbarButtonDefinition,
  IWidgetData,
} from '@shared/types';
import { BUTTON_FLEX_ORDER } from '@config/index';
import { I18n } from '@app/i18n/strings-keys';

export const isInfiniteRowModel = (context: Object, path: string): boolean => {
  return get(context, path) === GRID_ROW_MODEL_TYPE.INFINITE;
};

export class XuxGridActionButtonsHelper {
  public static setHiddenValueForButton(
    buttons: ToolbarButtonDefinition[],
    query: { [key: string]: any },
    value: boolean = false,
  ): void {
    const filteredButtons = filter<ToolbarButtonDefinition>(buttons, query);
    forEach(filteredButtons, (button) => set(button, 'hidden', value));
  }

  public static applyHideCriteriaForButtons(
    buttons: ToolbarButtonDefinition[],
    selectedItems: object[],
  ): ToolbarButtonDefinition[] {
    return reject<ToolbarButtonDefinition>(buttons, (button) => {
      if (isFunction(button.hidden)) {
        return invoke(button, 'hidden', selectedItems);
      }

      if (isBoolean(button.hidden)) {
        return button.hidden;
      }

      return false;
    }).map((button) => omit(button, ['hidden']));
  }

  public static applyDisableCriteriaForButtons(
    buttons: ToolbarButtonDefinition[],
    selectedItems: object[],
    disabled: boolean,
  ): ToolbarButtonDefinition[] {
    if (disabled) {
      return map(buttons, (button) => {
        return Object.assign(button, {
          disabled: get(button, 'disableWithGrid', true),
        });
      });
    }
    const calculateButtonDisabledState = (
      button: ToolbarButtonDefinition,
    ): ToolbarButtonDefinition => {
      const result = Object.assign({}, button);

      if (result.childItems) {
        result.childItems = map(
          result.childItems,
          calculateButtonDisabledState,
        );
      }

      if (!isFunction(result.disabled)) {
        return result;
      }

      return {
        ...result,
        disabled: invoke(result, 'disabled', selectedItems),
      };
    };

    return map(buttons, calculateButtonDisabledState);
  }

  public static updateChildActionButtons(
    actionButtons,
    selectedItems,
    disabled,
  ): ToolbarButtonDefinition[] {
    return actionButtons.map((button) => {
      if (!button.childItems) {
        return button;
      }

      let updatedButtons: ToolbarButtonDefinition[] = [...button.childItems];
      updatedButtons = XuxGridActionButtonsHelper.applyHideCriteriaForButtons(
        updatedButtons,
        selectedItems,
      );
      updatedButtons =
        XuxGridActionButtonsHelper.applyDisableCriteriaForButtons(
          updatedButtons,
          selectedItems,
          disabled,
        );

      return set(button, 'childItems', updatedButtons);
    });
  }

  public static applyCriteriaForActionButtons(
    actionButtonsInitial: ToolbarButtonDefinition[],
    selectedItems: object[],
    disabled: boolean,
  ): ToolbarButtonDefinition[] {
    let actionButtons: ToolbarButtonDefinition[] = actionButtonsInitial;
    actionButtons = XuxGridActionButtonsHelper.applyHideCriteriaForButtons(
      actionButtons,
      selectedItems,
    );
    actionButtons = XuxGridActionButtonsHelper.applyDisableCriteriaForButtons(
      actionButtons,
      selectedItems,
      disabled,
    );
    actionButtons = XuxGridActionButtonsHelper.updateChildActionButtons(
      actionButtons,
      selectedItems,
      disabled,
    );

    return actionButtons;
  }

  public static copyButtons(
    buttons: ToolbarButtonDefinition[],
  ): ToolbarButtonDefinition[] {
    return map(buttons, (button) => ({ ...button }));
  }

  public static getButtonsByPosition(
    actionButtons: ToolbarButtonDefinition[],
    position: TOOLBAR_BUTTON_POSITION,
  ): ToolbarButtonDefinition[] {
    if (position === TOOLBAR_BUTTON_POSITION.DEFAULT) {
      return filter(actionButtons, (button) => !has(button, 'position'));
    }

    return filter(
      actionButtons,
      (button) => get(button, 'position') === position,
    );
  }

  public static putButtonsActionToDropDown(
    buttons: ToolbarButtonDefinition[],
    options: ToolbarButtonDefinition = {},
  ): ToolbarButtonDefinition[] {
    return isEmpty(buttons)
      ? []
      : [
          {
            flexOrder: options.flexOrder || BUTTON_FLEX_ORDER.LEFTMOST,
            caption: options.caption || I18n.Common.Actions,
            value: options.value || 'actions',
            childItems: buttons,
          },
        ];
  }

  public static manageEditActionButton(
    actionButtons: ToolbarButtonDefinition[],
    widgetData: IWidgetData,
    selectedItems: object[],
  ): void {
    const singleItemSelected = get(selectedItems, 'length') === 1;
    if (!widgetData.canEdit || !singleItemSelected) {
      XuxGridActionButtonsHelper.setHiddenValueForButton(
        actionButtons,
        { value: BUTTON_TEMPLATES.GRID_EDIT_BTN.value },
        true,
      );
    }
  }

  public static manageRemoveActionButton(
    actionButtons: ToolbarButtonDefinition[],
    widgetData: IWidgetData,
    selectedItems: object[],
  ): void {
    const hasSelection = !isEmpty(selectedItems);
    if (!widgetData.canRemove || !hasSelection) {
      XuxGridActionButtonsHelper.setHiddenValueForButton(
        actionButtons,
        { value: BUTTON_TEMPLATES.GRID_REMOVE_BTN.value },
        true,
      );
    }
  }

  public static getDefinedByWidgetDataRulesButtons(
    widgetData: IWidgetData,
  ): ToolbarButtonDefinition[] {
    const buttons = [];

    if (!widgetData) {
      return buttons;
    }

    if (widgetData.canAdd) {
      buttons.push(BUTTON_TEMPLATES.GRID_CREATE_BTN);
      if (widgetData.canAddInPosition) {
        buttons.push(BUTTON_TEMPLATES.GRID_INSERT_AFTER);
        buttons.push(BUTTON_TEMPLATES.GRID_INSERT_BEFORE);
      }
    }

    if (widgetData.canEdit) {
      buttons.push(BUTTON_TEMPLATES.GRID_EDIT_BTN);
    }

    if (widgetData.canRemove) {
      buttons.push(BUTTON_TEMPLATES.GRID_REMOVE_BTN);
    }

    if (widgetData.advancedOrderControls) {
      buttons.push(BUTTON_TEMPLATES.GRID_MOVE_TO_TOP);
      buttons.push(BUTTON_TEMPLATES.GRID_MOVE_TO_BOTTOM);
    }

    return buttons;
  }

  public static prepareActionButtons(
    widgetData: IWidgetData,
    useButtonsDefinedByWidgetFlags: boolean = false,
  ): ToolbarButtonDefinition[] {
    const buttonsFromWidgetData = get(widgetData, 'buttons', []);

    let defaultActionButtons = [
      BUTTON_TEMPLATES.GRID_CREATE_BTN,
      BUTTON_TEMPLATES.GRID_EDIT_BTN,
      BUTTON_TEMPLATES.GRID_MOVE_TO_TOP,
      BUTTON_TEMPLATES.GRID_MOVE_TO_BOTTOM,
      BUTTON_TEMPLATES.GRID_INSERT_BEFORE,
      BUTTON_TEMPLATES.GRID_INSERT_AFTER,
      BUTTON_TEMPLATES.GRID_REMOVE_BTN,
    ];

    if (useButtonsDefinedByWidgetFlags) {
      defaultActionButtons =
        XuxGridActionButtonsHelper.getDefinedByWidgetDataRulesButtons(
          widgetData,
        );
    }

    const updatedDefaultButtons = defaultActionButtons.map((button) => {
      const buttonFromWidgetData =
        find(buttonsFromWidgetData, { value: button.value }) || {};

      return Object.assign({}, button, buttonFromWidgetData);
    });

    const newButtons = differenceBy(
      buttonsFromWidgetData,
      updatedDefaultButtons,
      'value',
    );
    const useCustomButtonsOnFirstPosition =
      widgetData?.useCustomButtonsOnFirstPosition ?? false;

    return useCustomButtonsOnFirstPosition
      ? [...newButtons, ...updatedDefaultButtons]
      : [...updatedDefaultButtons, ...newButtons];
  }

  public static invokeCustomClickHandler(
    actionButtons: ToolbarButtonDefinition[],
    buttonValue: string,
    rowData: object[],
  ): void {
    const clickedButton = find(actionButtons, { value: buttonValue });
    if (clickedButton) return invoke(clickedButton, 'click', rowData);

    const childButtons = compact(
      flatMap(actionButtons, (button) => button.childItems),
    );
    invoke(find(childButtons, { value: buttonValue }), 'click', rowData);
  }

  public static manageActionButtons(
    selectedItems: object[],
    availableActionButtons: ToolbarButtonDefinition[],
    widgetData: IWidgetData,
    disabled: boolean = false,
  ): ToolbarButtonDefinition[] {
    let actionButtons = XuxGridActionButtonsHelper.copyButtons(
      availableActionButtons,
    );

    XuxGridActionButtonsHelper.manageEditActionButton(
      actionButtons,
      widgetData,
      selectedItems,
    );
    XuxGridActionButtonsHelper.manageRemoveActionButton(
      actionButtons,
      widgetData,
      selectedItems,
    );

    actionButtons = XuxGridActionButtonsHelper.applyCriteriaForActionButtons(
      actionButtons,
      selectedItems,
      disabled,
    );

    return actionButtons;
  }
}
